import type {
  CSSProperties,
  FunctionComponent,
  HTMLAttributes,
  ReactElement,
  MouseEvent as ReactMouseEvent,
} from 'react';
import { useEffect, useRef, useState } from 'react';

import { useAudioTrigger } from '../../../controllers/audioController/hooks';
import { L10nText } from '../../../services/uiTranslations/reactBindings/L10nText';
import { useIsLegacyDesktop } from '../../../styles/hooks/legacyMedia';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { BackButton } from '../../controls/backButton/BackButton';
import { IconButton } from '../../controls/iconButton/IconButton';
import { Icons } from '../../icons/iconNames';
import { useLegacyContext } from '../../legacyMode/LegacyContextProvider';

import styles from './modal.module.css';

export interface ModalProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  open: boolean;
  draggable?: boolean;
  footer?: ReactElement;
  fullScreenNoClose?: boolean;
  grow?: boolean;
  heightAdjustment?: number;
  isHistoryModal?: boolean;
  large?: boolean;
  limitedHeight?: string;
  maxBodyHeight?: number;
  onBack?: () => void;
  onClose?: () => void;
  placement?: 'center' | 'start' | 'end';
  small?: boolean;
  title?: ReactElement | string;
  tk?: string;
}

interface Vector {
  x: number;
  y: number;
}

export const Modal: FunctionComponent<ModalProps> = ({
  children,
  draggable,
  footer,
  fullScreenNoClose,
  grow,
  heightAdjustment,
  large,
  limitedHeight,
  maxBodyHeight,
  onBack,
  onClose,
  open,
  placement = 'center',
  small,
  title,
  tk,
  ...rest
}): ReactElement => {
  const audioTrigger = useAudioTrigger();
  const [shouldRender, setShouldRender] = useState(open);
  const modalRef = useRef<HTMLDivElement>(null);
  const modalWindowRef = useRef<HTMLDivElement>(null);
  const { isLegacyMode } = useLegacyContext();
  const isLegacyDesktop = useIsLegacyDesktop();

  const mods = useModifiers(
    styles,
    'modal',
    {
      small,
      large,
      draggable,
      grow,
      open,
      limitedHeight: !!limitedHeight,
      start: placement === 'start',
      end: placement === 'end',
    },
    { withBaseClass: true }
  );
  const [offset, setOffset] = useState<Vector>({ x: 0, y: 0 });

  useEffect(() => {
    let timeoutId: number;
    if (open) {
      setShouldRender(true);
      setOffset({ x: 0, y: 0 });
    } else {
      modalRef?.current?.classList.add(styles.modal_closing);
      timeoutId = window.setTimeout(() => {
        modalRef?.current?.classList.remove(styles.modal_closing);
        setShouldRender(false);
      }, 300);
    }
    return (): void => window.clearTimeout(timeoutId);
  }, [open]);

  const handlePointerDown = (event: ReactMouseEvent<HTMLDivElement>): void => {
    if (!draggable || !modalRef.current) return;

    let offsetVector: Vector = { ...offset };
    const clickOriginVector: Vector = {
      x: event.clientX,
      y: event.clientY,
    };

    const clampOffset = (offset: Vector): Vector => {
      if (offset.y < 0) {
        offset.y = 0;
      }
      return offset;
    };

    const updateOffsetVector = (event: MouseEvent): void => {
      offsetVector = clampOffset({
        x: event.clientX - clickOriginVector.x + offset.x,
        y: event.clientY - clickOriginVector.y + offset.y,
      });
      setOffset(offsetVector);
    };

    const handlePointerMove = (event: MouseEvent): void => {
      updateOffsetVector(event);
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    const handlePointerUp = (): void => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
    window.addEventListener('pointerup', handlePointerUp);
  };

  const handleClose = (): void => {
    onClose?.();
  };

  const handleBack = (): void => {
    if (onBack) {
      onBack();
    } else {
      handleClose();
    }
    audioTrigger.play({ name: 'menuClick' });
  };

  return (
    <div
      data-testid="modal"
      className={mods}
      style={
        {
          '--heightAdjustment': `${heightAdjustment ?? 0}px`,
          '--max-height': limitedHeight ?? '100%',
        } as CSSProperties
      }
      ref={modalRef}
    >
      {shouldRender ? (
        <div
          data-testid="modal-window"
          className={styles.modal__window}
          style={
            {
              transform: draggable ? `translate(${Math.round(offset.x)}px,  ${Math.round(offset.y)}px)` : null,
            } as CSSProperties
          }
          ref={modalWindowRef}
          {...rest}
        >
          <div data-testid="modal-header" className={styles.modal__header} onPointerDown={handlePointerDown}>
            <div className={styles.modal__headerWing}>
              <BackButton onClick={handleBack} visible={!!onBack} />
            </div>
            <div data-testid="modal-header-title" className={styles.modal__headerTitle}>
              {tk ? <L10nText tk={tk} /> : title}
            </div>
            <div className={styles.modal__headerWing}>
              <div className={`${fullScreenNoClose ? styles.modal__action_fullScreenHidden : ''}`}>
                {onClose ? (
                  <IconButton
                    data-testid="modal-close-button"
                    ghost
                    icon={Icons.Close}
                    onClick={handleClose}
                    {...(isLegacyMode && isLegacyDesktop ? { small: true } : { tiny: true })}
                  />
                ) : null}
              </div>
            </div>
          </div>
          <div data-testid="modal-body" className={styles.modal__body} style={{ maxHeight: maxBodyHeight }}>
            {children}
          </div>
          {footer ? (
            <div data-testid="modal-footer" className={styles.modal__footer}>
              {footer}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
